import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email as EmailIcon,
  Star as StarIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useGmail } from '../../hooks/useGmail';
import { useDatabase } from '../../hooks/useDatabase';
import { EmailMetadata, EmailFilters } from '../../types/gmail';
import { Contact } from '../../types/database';

interface GmailInboxProps {
  onEmailClick?: (email: EmailMetadata) => void;
  query?: string;
  searchTerm?: string;
}

export function GmailInbox({ onEmailClick, query, searchTerm }: GmailInboxProps) {
  const { loading, error, listEmails, clearError } = useGmail();
  const db = useDatabase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [emails, setEmails] = useState<EmailMetadata[]>([]);
  const [filters] = useState<EmailFilters>({
    maxResults: 20,
    labelIds: ['INBOX']
  });
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextPageTokenRef = useRef<string | null>(null);
  const [searchQuery] = useState('');
  const [selectedLabel] = useState('INBOX');
  const [contactStatuses, setContactStatuses] = useState<Record<string, 'unknown' | 'exists' | 'added' | 'adding'>>({});
  const [contactIds, setContactIds] = useState<Record<string, string>>({});
  const [similarContactDialog, setSimilarContactDialog] = useState<{
    open: boolean;
    email: string;
    name: string;
    similarContacts: Contact[];
  }>({
    open: false,
    email: '',
    name: '',
    similarContacts: []
  });

  const loadEmails = useCallback(async (loadMore = false) => {
    if (loadMore && loadingMore) return; // Prevent multiple simultaneous requests
    
    const emailFilters: EmailFilters = {
      ...filters,
      labelIds: [selectedLabel],
      query: query || searchTerm || searchQuery || undefined,
      pageToken: loadMore && nextPageTokenRef.current ? nextPageTokenRef.current : undefined
    };

    if (loadMore) {
      setLoadingMore(true);
    }

    try {
      const result = await listEmails(emailFilters);
      console.log('Loaded emails:', result.emails.length, 'emails');
      
      if (loadMore) {
        // Append new emails to existing list
        setEmails(prevEmails => [...prevEmails, ...result.emails]);
      } else {
        // Replace emails list (initial load or refresh)
        setEmails(result.emails);
      }
      
      // Update nextPageToken and ref
      const newToken = result.nextPageToken || null;
      setNextPageToken(newToken);
      nextPageTokenRef.current = newToken;
      
      clearError();
    } catch (error) {
      console.error('Error loading emails:', error);
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      }
    }
  }, [filters, selectedLabel, query, searchTerm, searchQuery, listEmails, clearError, loadingMore]);

  // Load emails when component mounts or filters change (but not when nextPageToken changes)
  useEffect(() => {
    loadEmails(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLabel, query, searchTerm, searchQuery]);

  // Update ref when nextPageToken changes
  useEffect(() => {
    nextPageTokenRef.current = nextPageToken;
  }, [nextPageToken]);

  const handleRefresh = () => {
    setNextPageToken(null);
    nextPageTokenRef.current = null;
    loadEmails(false);
  };

  const handleLoadMore = () => {
    loadEmails(true);
  };

  // Check if contact exists for an email
  const checkContactExists = useCallback(async (email: string) => {
    try {
      const result = await db.getContactByEmail(email);
      if (result.data) {
        setContactStatuses(prev => ({ ...prev, [email]: 'exists' }));
        setContactIds(prev => ({ ...prev, [email]: result.data!.id }));
      } else {
        setContactStatuses(prev => ({ ...prev, [email]: 'unknown' }));
      }
    } catch (err) {
      console.error('Error checking contact:', err);
      setContactStatuses(prev => ({ ...prev, [email]: 'unknown' }));
    }
  }, [db]);

  // Find contacts with similar names
  const findSimilarContacts = useCallback((name: string, allContacts: Contact[]) => {
    const normalizedName = name.toLowerCase().trim();
    const nameWords = normalizedName.split(' ').filter(word => word.length > 2);
    
    return allContacts.filter(contact => {
      const contactName = contact.name.toLowerCase().trim();
      const contactWords = contactName.split(' ').filter(word => word.length > 2);
      
      // Check for exact match
      if (contactName === normalizedName) return true;
      
      // Check for partial word matches
      const matchingWords = nameWords.filter(word => 
        contactWords.some(contactWord => 
          contactWord.includes(word) || word.includes(contactWord)
        )
      );
      
      // Consider similar if at least 50% of words match
      return matchingWords.length >= Math.ceil(nameWords.length / 2);
    });
  }, []);

  // Extract email address from "Name <email>" format
  const extractEmailAddress = useCallback((fromString: string) => {
    // Check if it's in "Name <email>" format
    const match = fromString.match(/<([^>]+)>/);
    if (match) {
      return match[1]; // Return the email address inside the brackets
    }
    // If no brackets, assume the whole string is the email
    return fromString;
  }, []);

  // Extract name from email address or "Name <email>" format
  const extractNameFromEmail = useCallback((fromString: string, emailAddress: string) => {
    // Check if it's in "Name <email>" format
    const match = fromString.match(/^([^<]+)<[^>]+>$/);
    if (match) {
      return match[1].trim(); // Return the name part, trimmed
    }
    
    // If no name format, extract from email address
    const localPart = emailAddress.split('@')[0];
    return localPart.replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  // Add contact from email
  const handleAddContact = useCallback(async (email: EmailMetadata) => {
    const emailAddress = extractEmailAddress(email.from);
    
    // Check if already exists
    const existingStatus = contactStatuses[emailAddress];
    if (existingStatus === 'exists' || existingStatus === 'added') {
      return; // Already exists or was added
    }

    setContactStatuses(prev => ({ ...prev, [emailAddress]: 'adding' }));

    try {
      // First check if contact already exists
      const existingContact = await db.getContactByEmail(emailAddress);
      if (existingContact.data) {
        setContactStatuses(prev => ({ ...prev, [emailAddress]: 'exists' }));
        return;
      }

      // Extract name from email or from string
      const name = extractNameFromEmail(email.from, emailAddress);

      // Get all contacts to check for similar names
      const allContactsResult = await db.getContacts();
      if (allContactsResult.data) {
        const similarContacts = findSimilarContacts(name, allContactsResult.data);
        
        if (similarContacts.length > 0) {
          // Show dialog for similar contacts
          setContactStatuses(prev => ({ ...prev, [emailAddress]: 'unknown' }));
          setSimilarContactDialog({
            open: true,
            email: emailAddress,
            name,
            similarContacts
          });
          return;
        }
      }

      // Create new contact if no similar contacts found
      const newContact = await db.createContact({
        name,
        email: emailAddress,
        tags: {},
        associated_contacts: [],
        relationship_types: {},
        user_id: '', // This will be set by the database function
      });

      if (newContact.data) {
        setContactStatuses(prev => ({ ...prev, [emailAddress]: 'added' }));
        setContactIds(prev => ({ ...prev, [emailAddress]: newContact.data!.id }));
        console.log('Contact added successfully:', newContact.data);
        
        // Trigger networking suggestions recalculation after adding contact from Gmail
        // Debounce to avoid multiple recalculations if multiple contacts are added quickly
        setTimeout(async () => {
          try {
            const { error: recalcError } = await db.recalculateNetworkingSuggestions();
            if (recalcError) {
              console.error('Error recalculating networking suggestions after Gmail contact addition:', recalcError);
            }
          } catch (err) {
            console.error('Error triggering networking suggestions recalculation:', err);
          }
        }, 2000); // 2 second debounce
      } else {
        setContactStatuses(prev => ({ ...prev, [emailAddress]: 'unknown' }));
        console.error('Failed to add contact:', newContact.error);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      setContactStatuses(prev => ({ ...prev, [emailAddress]: 'unknown' }));
    }
  }, [db, contactStatuses, findSimilarContacts, extractEmailAddress, extractNameFromEmail]);

  // Handle linking to existing contact
  const handleLinkToExistingContact = useCallback(async (contactId: string) => {
    const { email } = similarContactDialog;
    setContactStatuses(prev => ({ ...prev, [email]: 'adding' }));
    
    try {
      // Update the existing contact with the email if it doesn't have one
      const existingContact = await db.getContactById(contactId);
      if (existingContact.data && !existingContact.data.email) {
        await db.updateContact(contactId, { email });
      }
      
      setContactStatuses(prev => ({ ...prev, [email]: 'exists' }));
      setContactIds(prev => ({ ...prev, [email]: contactId }));
      setSimilarContactDialog({ open: false, email: '', name: '', similarContacts: [] });
    } catch (err) {
      console.error('Error linking contact:', err);
      setContactStatuses(prev => ({ ...prev, [email]: 'unknown' }));
    }
  }, [db, similarContactDialog]);

  // Handle creating new contact anyway
  const handleCreateNewContact = useCallback(async () => {
    const { email, name } = similarContactDialog;
    const emailAddress = extractEmailAddress(email);
    setContactStatuses(prev => ({ ...prev, [email]: 'adding' }));
    
    try {
      const newContact = await db.createContact({
        name,
        email: emailAddress,
        tags: {},
        associated_contacts: [],
        relationship_types: {},
        user_id: '',
      });

      if (newContact.data) {
        setContactStatuses(prev => ({ ...prev, [email]: 'added' }));
        setContactIds(prev => ({ ...prev, [email]: newContact.data!.id }));
      } else {
        setContactStatuses(prev => ({ ...prev, [email]: 'unknown' }));
      }
      
      setSimilarContactDialog({ open: false, email: '', name: '', similarContacts: [] });
    } catch (err) {
      console.error('Error creating contact:', err);
      setContactStatuses(prev => ({ ...prev, [email]: 'unknown' }));
    }
  }, [db, similarContactDialog, extractEmailAddress]);

  // Handle viewing contact
  const handleViewContact = useCallback((email: string) => {
    const contactId = contactIds[email];
    if (contactId) {
      // Navigate to contact detail page
      window.open(`/contacts/${contactId}`, '_blank');
    }
  }, [contactIds]);

  // Check contact status when emails load
  useEffect(() => {
    emails.forEach(email => {
      const emailAddress = extractEmailAddress(email.from);
      if (!contactStatuses[emailAddress]) {
        checkContactExists(emailAddress);
      }
    });
  }, [emails, checkContactExists, contactStatuses, extractEmailAddress]);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const cleanSnippet = (snippet: string) => {
    // First decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = snippet;
    const decodedSnippet = textarea.value;
    
    // Then remove common image file extensions and patterns
    return decodedSnippet
      .replace(/image\d+\.(jpeg|jpg|png|gif|bmp|webp)/gi, '')
      .replace(/attachment\d*\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|zip|rar)/gi, '')
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  };

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Email List - No custom scrolling */}
        <Box sx={{ flexGrow: 1 }}>
          {loading && emails.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : emails.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No emails found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or filters
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ bgcolor: 'background.paper', p: 0 }}>
                {emails.map((email, index) => (
                  <React.Fragment key={email.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        },
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        px: isMobile ? 1 : 2,
                        py: isMobile ? 1.5 : 1
                      }}
                      onClick={() => onEmailClick?.(email)}
                    >
                      {/* Top row for mobile */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%',
                        mb: isMobile ? 1 : 0
                      }}>
                        <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: isMobile ? 28 : 32,
                              height: isMobile ? 28 : 32,
                              fontSize: isMobile ? '0.75rem' : '0.875rem'
                            }}
                          >
                            {email.from.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemIcon>

                        <ListItemText
                          sx={{ flexGrow: 1 }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant={email.isUnread ? 'subtitle2' : 'body2'}
                                sx={{
                                  fontWeight: email.isUnread ? 600 : 400,
                                  flexGrow: 1,
                                  fontSize: isMobile ? '0.8rem' : undefined,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {email.from}
                              </Typography>
                              
                              {email.isStarred && (
                                <StarIcon color="warning" fontSize="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            !isMobile && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: email.isUnread ? 600 : 400,
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {email.subject}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {truncateText(cleanSnippet(email.snippet), 100)}
                                </Typography>
                              </Box>
                            )
                          }
                        />

                        {!isMobile && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(email.date)}
                            </Typography>

                            {/* Contact Action Buttons - View Contact First */}
                            {(() => {
                              const emailAddress = extractEmailAddress(email.from);
                              return contactStatuses[emailAddress] === 'exists' || contactStatuses[emailAddress] === 'added' ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewContact(emailAddress);
                                  }}
                                  title="View Contact"
                                >
                                  <SearchIcon 
                                    fontSize="small" 
                                    sx={{ color: 'success.main' }}
                                  />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddContact(email);
                                  }}
                                  title={
                                    contactStatuses[emailAddress] === 'adding'
                                      ? "Adding contact..."
                                      : "Add as Contact"
                                  }
                                  disabled={contactStatuses[emailAddress] === 'adding'}
                                >
                                  {contactStatuses[emailAddress] === 'adding' ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <PersonAddIcon fontSize="small" />
                                  )}
                                </IconButton>
                              );
                            })()}

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEmailClick?.(email);
                              }}
                              title="View Email"
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      {/* Mobile layout - Subject and actions below */}
                      {isMobile && (
                        <Box sx={{ 
                          width: '100%', 
                          pl: 7, // Align with text content
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start'
                        }}>
                          <Box sx={{ flexGrow: 1, mr: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: email.isUnread ? 600 : 400,
                                mb: 0.5,
                                fontSize: '0.85rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {email.subject}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', fontSize: '0.75rem' }}
                            >
                              {truncateText(cleanSnippet(email.snippet), 80)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {formatDate(email.date)}
                            </Typography>
                          </Box>

                          {/* Mobile action buttons */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {(() => {
                              const emailAddress = extractEmailAddress(email.from);
                              return contactStatuses[emailAddress] === 'exists' || contactStatuses[emailAddress] === 'added' ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewContact(emailAddress);
                                  }}
                                  title="View Contact"
                                >
                                  <SearchIcon 
                                    fontSize="small" 
                                    sx={{ color: 'success.main', fontSize: '1rem' }}
                                  />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddContact(email);
                                  }}
                                  title={
                                    contactStatuses[emailAddress] === 'adding'
                                      ? "Adding contact..."
                                      : "Add as Contact"
                                  }
                                  disabled={contactStatuses[emailAddress] === 'adding'}
                                >
                                  {contactStatuses[emailAddress] === 'adding' ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <PersonAddIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                  )}
                                </IconButton>
                              );
                            })()}

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEmailClick?.(email);
                              }}
                              title="View Email"
                            >
                              <EmailIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </ListItem>
                    {index < emails.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* Load More Button */}
              {nextPageToken && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore || loading}
                    startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                    sx={{ minWidth: 120 }}
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Similar Contact Confirmation Dialog */}
      <Dialog 
        open={similarContactDialog.open} 
        onClose={() => setSimilarContactDialog({ open: false, email: '', name: '', similarContacts: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Similar Contact Found
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We found {similarContactDialog.similarContacts.length} contact{similarContactDialog.similarContacts.length > 1 ? 's' : ''} with similar names to "{similarContactDialog.name}":
          </Typography>
          
          {similarContactDialog.similarContacts.map((contact) => (
            <Paper key={contact.id} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {contact.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {contact.name}
                  </Typography>
                  {contact.email && (
                    <Typography variant="body2" color="text.secondary">
                      {contact.email}
                    </Typography>
                  )}
                  {/* Note: Company removed - use organizations instead (would require additional query) */}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleLinkToExistingContact(contact.id)}
                >
                  Link Email
                </Button>
              </Box>
            </Paper>
          ))}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Would you like to link the email "{similarContactDialog.email}" to one of these existing contacts, or create a new contact?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSimilarContactDialog({ open: false, email: '', name: '', similarContacts: [] })}
          >
            Cancel
          </Button>
          <Button 
            variant="outlined"
            onClick={handleCreateNewContact}
          >
            Create New Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
